import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

const AppDialog = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  primaryLabel = "Confirm",
  secondaryLabel = "Cancel",
  onPrimary,
  onSecondary,
  primaryDisabled = false,
  primaryLoading = false,
  maxWidth = "sm",
  fullWidth = true,
  hideSecondary = false,
  fullScreenOnMobile = true,
  showCloseIcon = true,
  headerIcon = null,
  variant = "default", // "default" | "info" | "success" | "warning" | "danger"
  alignActions = "right", // "right" | "center" | "stretch"
  eyebrow, // optional small label above title
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getVariantColors = () => {
    switch (variant) {
      case "info":
        return {
          accent: theme.palette.info.main,
          footerBg: theme.palette.background.default,
          labelBg: theme.palette.mode === "dark" ? "rgba(33, 150, 243, 0.15)" : "rgba(33, 150, 243, 0.08)",
          labelColor: theme.palette.info.main,
        };
      case "success":
        return {
          accent: theme.palette.success.main,
          footerBg: theme.palette.background.default,
          labelBg: theme.palette.mode === "dark" ? "rgba(76, 175, 80, 0.18)" : "rgba(76, 175, 80, 0.08)",
          labelColor: theme.palette.success.main,
        };
      case "warning":
        return {
          accent: theme.palette.warning.main,
          footerBg: theme.palette.background.default,
          labelBg: theme.palette.mode === "dark" ? "rgba(255, 167, 38, 0.18)" : "rgba(255, 167, 38, 0.1)",
          labelColor: theme.palette.warning.main,
        };
      case "danger":
        return {
          accent: theme.palette.error.main,
          footerBg: theme.palette.background.default,
          labelBg: theme.palette.mode === "dark" ? "rgba(244, 67, 54, 0.18)" : "rgba(244, 67, 54, 0.1)",
          labelColor: theme.palette.error.main,
        };
      default:
        return {
          accent: theme.palette.primary.main,
          footerBg: theme.palette.background.default,
          labelBg: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : theme.palette.grey[100],
          labelColor: theme.palette.text.secondary,
        };
    }
  };

  const { accent, footerBg, labelBg, labelColor } = getVariantColors();

  const handleSecondary = () => {
    if (onSecondary) {
      onSecondary();
    } else if (onClose) {
      onClose();
    }
  };

  const handlePrimary = () => {
    if (onPrimary) {
      onPrimary();
    }
  };

  const justifyContent =
    alignActions === "center" ? "center" : alignActions === "stretch" ? "space-between" : "flex-end";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreenOnMobile && isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          boxShadow: 16,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      {(title || subtitle) && (
        <DialogTitle
          sx={{
            px: 3,
            py: 3,
            pr: showCloseIcon ? 6 : 3,
            display: "flex",
            alignItems: headerIcon ? "flex-start" : "center",
            gap: 2,
            position: "relative",
          }}
        >
          {headerIcon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: theme.palette.action.hover,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: accent,
                flexShrink: 0,
              }}
            >
              {headerIcon}
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={0.75}>
              {(eyebrow || variant !== "default") && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 1.3,
                    py: 0.4,
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.08,
                    bgcolor: labelBg,
                    color: labelColor,
                  }}
                >
                  {eyebrow ||
                    (variant === "info"
                      ? "Info"
                      : variant === "success"
                      ? "Success"
                      : variant === "warning"
                      ? "Warning"
                      : variant === "danger"
                      ? "Attention"
                      : "Notice")}
                </Box>
              )}
              {title && (
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ wordBreak: "break-word", letterSpacing: 0.1 }}
                >
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="body1" >
                  {subtitle}
                </Typography>
              )}
            </Stack>
          </Box>
          {showCloseIcon && onClose && (
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close dialog"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>
      )}
      {children && (
        <DialogContent
          sx={{
            px: 3,
            pt: title || subtitle ? 1.5 : 3,
            pb: 2.5,
          }}
        >
          {children}
        </DialogContent>
      )}
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.75,
          gap: 1.5,
          justifyContent: isMobile ? "stretch" : justifyContent,
          flexWrap: isMobile ? "wrap" : "nowrap",
          bgcolor: footerBg,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        {!hideSecondary && (
          <Button
            onClick={handleSecondary}
            variant="outlined"
            disabled={primaryLoading}
            fullWidth={isMobile || alignActions === "stretch"}
          >
            {secondaryLabel}
          </Button>
        )}
        <Button
          onClick={handlePrimary}
          variant="contained"
          disabled={primaryDisabled || primaryLoading}
          fullWidth={isMobile || alignActions === "stretch"}
        >
          {primaryLoading ? "Processing..." : primaryLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppDialog;

